/**
 * DatabaseExportCard — Admin-Karte für den kompletten Datenbank-Export (Backup).
 *
 * Lädt die gesamte Datenbank als JSON (vollständiges Backup) oder Excel
 * (ein Tabellenblatt pro Sammlung) herunter. Nur für Admins (settings: edit).
 */
import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Database, FileJson, FileSpreadsheet, Loader2, Info, Upload, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const RESET_CONFIRM_TEXT = 'TESTDATEN LOESCHEN';

export const DatabaseExportCard = () => {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null); // 'json' | 'xlsx' | null
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetting, setResetting] = useState(false);

  const loadMeta = () => {
    api.get('/admin/db-export/meta')
      .then((res) => setMeta(res.data))
      .catch(() => setMeta(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadMeta(); }, []);

  const download = async (format) => {
    setDownloading(format);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/db-export/${format}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Export fehlgeschlagen');
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `db_export.${format}`;
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
      toast.success('Datenbank-Export heruntergeladen');
      return true;
    } catch (e) {
      toast.error('Fehler beim Datenbank-Export');
      return false;
    } finally {
      setDownloading(null);
    }
  };

  const handleFactoryReset = async () => {
    if (resetConfirm.trim() !== RESET_CONFIRM_TEXT) {
      toast.error(`Bitte exakt "${RESET_CONFIRM_TEXT}" eingeben.`);
      return;
    }
    if (!resetPassword) {
      toast.error('Bitte dein Admin-Passwort eingeben.');
      return;
    }
    setResetting(true);
    try {
      // Schritt 1: Automatisches Backup herunterladen (Sicherheitsnetz).
      const backupOk = await download('json');
      if (!backupOk) {
        toast.error('Backup fehlgeschlagen — Reset abgebrochen.');
        setResetting(false);
        return;
      }
      // Schritt 2: Reset ausführen.
      const res = await api.post('/admin/db-export/factory-reset', {
        password: resetPassword,
        confirm_text: resetConfirm.trim(),
      });
      toast.success(
        `Zurückgesetzt · ${res.data.deleted_documents.toLocaleString('de-DE')} Datensätze entfernt. Nur dein Admin-Login bleibt.`
      );
      setResetOpen(false);
      setResetPassword('');
      setResetConfirm('');
      // Kurze Verzögerung, dann neu laden, damit die geleerte DB überall greift.
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset fehlgeschlagen');
    } finally {
      setResetting(false);
    }
  };

  const handleImportSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // erlaubt erneute Auswahl derselben Datei
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.json')) {
      toast.error('Bitte eine JSON-Backup-Datei auswählen');
      return;
    }
    const ok = window.confirm(
      `Backup „${file.name}" jetzt importieren?\n\n` +
      'Bestehende Datensätze werden anhand ihrer ID aktualisiert, fehlende neu angelegt ' +
      '(nichts wird gelöscht). Benutzerkonten/Logins bleiben unberührt.'
    );
    if (!ok) return;

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/db-export/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Import abgeschlossen · ${res.data.collections_imported} Sammlungen wiederhergestellt`);
      loadMeta();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Import');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Card className="border-border/60" data-testid="db-export-card">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Database className="h-5 w-5 text-primary" />
          Datenbank-Export (Backup)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Lädt die komplette Datenbank herunter — als <strong>JSON</strong> (vollständiges Backup zur
          Wiederherstellung) oder als <strong>Excel</strong> (ein Tabellenblatt pro Sammlung, zum Ansehen/Auswerten).
        </p>

        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Der Export enthält <strong>sensible Daten</strong> (z. B. Bankverbindungen, IT-Zugänge). Bitte sicher aufbewahren
            und nur an berechtigte Personen weitergeben.
          </span>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Lade Übersicht…</p>
        ) : meta ? (
          <p className="text-sm text-muted-foreground" data-testid="db-export-summary">
            {meta.collections.length} Sammlungen · {meta.total_documents.toLocaleString('de-DE')} Datensätze
          </p>
        ) : null}

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => download('json')}
            disabled={downloading !== null}
            className="rounded-full"
            data-testid="db-export-json-btn"
          >
            {downloading === 'json' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileJson className="mr-2 h-4 w-4" />}
            Als JSON exportieren
          </Button>
          <Button
            onClick={() => download('xlsx')}
            disabled={downloading !== null}
            variant="secondary"
            className="rounded-full"
            data-testid="db-export-xlsx-btn"
          >
            {downloading === 'xlsx' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}
            Als Excel exportieren
          </Button>
        </div>

        <div className="border-t border-border/60 pt-4 space-y-3">
          <div>
            <p className="text-sm font-medium">Backup wiederherstellen (Import)</p>
            <p className="text-xs text-muted-foreground mt-1">
              Spielt eine zuvor exportierte <strong>JSON-Datei</strong> wieder ein. Datensätze werden anhand ihrer ID
              aktualisiert bzw. neu angelegt — <strong>nichts wird gelöscht</strong>. Benutzerkonten/Logins bleiben zum Schutz unberührt.
            </p>
          </div>
          <input
            ref={importInputRef}
            type="file"
            accept=".json,application/json"
            onChange={handleImportSelect}
            className="hidden"
          />
          <Button
            onClick={() => importInputRef.current?.click()}
            disabled={importing}
            variant="outline"
            className="rounded-full"
            data-testid="db-import-btn"
          >
            {importing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            Backup importieren (JSON)
          </Button>
        </div>

        {/* ==================== DANGER ZONE ==================== */}
        <div className="border-t-2 border-red-200 pt-4 space-y-3">
          <div>
            <p className="text-sm font-semibold text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Testdaten entfernen (Werkseinstellung)
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Löscht <strong>alle</strong> Mitarbeiter, Test-Logins, Standorte und operativen Daten.
              Es bleibt <strong>nur dein Admin-Login</strong> erhalten; System-Rollen werden auf Standard
              zurückgesetzt. Vor dem Löschen wird automatisch ein <strong>JSON-Backup heruntergeladen</strong>.
              Diese Aktion ist <strong>unwiderruflich</strong>.
            </p>
          </div>
          <Button
            onClick={() => setResetOpen(true)}
            variant="destructive"
            className="rounded-full"
            data-testid="db-factory-reset-btn"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Testdaten entfernen
          </Button>
        </div>

        <Dialog open={resetOpen} onOpenChange={(o) => { if (!resetting) setResetOpen(o); }}>
          <DialogContent data-testid="db-factory-reset-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                Datenbank komplett zurücksetzen?
              </DialogTitle>
              <DialogDescription className="space-y-2 pt-2">
                <span className="block">
                  Es werden <strong>alle Daten unwiderruflich gelöscht</strong> — nur dein Admin-Login
                  (<strong>{meta ? `${meta.total_documents.toLocaleString('de-DE')} Datensätze` : 'alle Datensätze'}</strong> betroffen) bleibt erhalten.
                </span>
                <span className="block">
                  Vor dem Löschen wird automatisch ein vollständiges <strong>JSON-Backup</strong> heruntergeladen.
                </span>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="reset-password">Dein Admin-Passwort</Label>
                <Input
                  id="reset-password"
                  type="password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="db-factory-reset-password"
                  autoComplete="current-password"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="reset-confirm">
                  Zur Bestätigung <span className="font-mono font-semibold">{RESET_CONFIRM_TEXT}</span> eingeben
                </Label>
                <Input
                  id="reset-confirm"
                  value={resetConfirm}
                  onChange={(e) => setResetConfirm(e.target.value)}
                  placeholder={RESET_CONFIRM_TEXT}
                  data-testid="db-factory-reset-confirm"
                  autoComplete="off"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => setResetOpen(false)}
                disabled={resetting}
                data-testid="db-factory-reset-cancel"
              >
                Abbrechen
              </Button>
              <Button
                variant="destructive"
                className="rounded-full"
                onClick={handleFactoryReset}
                disabled={resetting || resetConfirm.trim() !== RESET_CONFIRM_TEXT || !resetPassword}
                data-testid="db-factory-reset-confirm-btn"
              >
                {resetting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                {resetting ? 'Wird zurückgesetzt…' : 'Endgültig löschen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
